/**
 * SEO + AEO content for feature landing pages.
 * Each feature gets unique meta, headings, FAQs, internal links, and schema.
 */

export type FeatureSlug =
  | 'pomodoro-timer'
  | 'deep-work'
  | 'focus-sounds'
  | 'task-manager'
  | 'productivity-tools'
  | 'distraction-blocker'
  | 'work-session-timer';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface HowToStep {
  name: string;
  text: string;
}

export interface FeatureSEOConfig {
  slug: FeatureSlug;
  title: string;
  description: string;
  h1: string;
  intro: string;
  h2Sections: { heading: string; content: string }[];
  faqs: FAQItem[];
  internalLinks: { label: string; path: string }[];
  ctaTitle: string;
  ctaDescription: string;
  /** HowTo schema steps for rich search results */
  howToSteps?: HowToStep[];
  /** Screenshot for visual SEO */
  screenshot?: { src: string; alt: string };
  /** Related productivity guides (blog or feature pages) */
  relatedGuides?: { label: string; path: string }[];
}

const FEATURE_SEO_DATA: Record<FeatureSlug, FeatureSEOConfig> = {
  'pomodoro-timer': {
    slug: 'pomodoro-timer',
    title: 'Pomodoro Timer | Focuzon',
    description: 'Learn the Pomodoro timer technique to stay focused and complete tasks using structured work sessions and deep work techniques.',
    h1: 'Pomodoro Timer',
    intro: 'The Pomodoro timer technique helps you stay focused and complete tasks using structured work sessions and deep work techniques.',
    h2Sections: [
      {
        heading: 'What is Pomodoro Timer Technique',
        content: 'The Pomodoro Technique is a time management method that breaks work into focused intervals, typically 25 minutes, followed by short breaks. It was developed by Francesco Cirillo in the late 1980s to improve concentration and reduce burnout. Each interval is called a pomodoro, named after the tomato-shaped timer Cirillo used.',
      },
      {
        heading: 'Benefits of Pomodoro Timer',
        content: 'A Pomodoro timer helps you maintain focus, reduce procrastination, and track productivity. Structured work blocks reduce decision fatigue and create a sense of urgency. Short breaks prevent mental fatigue while longer breaks after several pomodoros help sustain energy throughout the day.',
      },
      {
        heading: 'How to Use Pomodoro Timer',
        content: 'Choose a task, set the timer for 25 minutes, and work without interruption until the timer rings. Take a 5-minute break, then start another pomodoro. After four pomodoros, take a 15–30 minute break. Focuzon lets you customize session length and tracks your completed sessions automatically.',
      },
      {
        heading: 'Start a Pomodoro Session',
        content: 'Ready to focus? Click below to start a Pomodoro session with Focuzon. Set your goal, choose your focus duration, and begin working in structured intervals. Sign up to get started and save your progress.',
      },
    ],
    faqs: [
      {
        question: 'What is a Pomodoro timer?',
        answer: 'A Pomodoro timer is a productivity tool that divides work into focused intervals, typically 25 minutes, followed by short 5-minute breaks. Developed by Francesco Cirillo, it helps improve concentration and reduce burnout by creating a clear rhythm of work and rest. Each interval trains your brain to sustain focus, and the breaks prevent mental fatigue. Focuzon offers a free Pomodoro timer with customizable lengths.',
      },
      {
        question: 'How long is a Pomodoro session?',
        answer: 'A standard Pomodoro session is 25 minutes of focused work followed by a 5-minute short break. After completing four sessions, take a longer 15–30 minute break to fully recharge. You can adjust these lengths in Focuzon to match your rhythm. Some people prefer 20-minute or 45-minute work blocks depending on their attention span and task type.',
      },
      {
        question: 'Is the Focuzon Pomodoro timer free?',
        answer: 'Yes. Focuzon offers a completely free Pomodoro timer with customizable session lengths, ambient focus sounds, and progress tracking. No credit card or subscription is required. You can start using it immediately for structured work blocks. Optional account creation lets you save session history and sync across devices, but the core timer works without signing up.',
      },
      {
        question: 'Can I customize Pomodoro length?',
        answer: 'Yes. Focuzon allows you to set custom work intervals and break durations to match your focus style and workflow. You are not locked into the classic 25/5 pattern. For longer tasks, try 45-minute work blocks with 10-minute breaks. For shorter bursts, 15-minute sessions can work well. Adjust settings based on what helps you maintain concentration.',
      },
      {
        question: 'Does Pomodoro timer improve productivity?',
        answer: 'Research and user reports suggest the Pomodoro Technique improves focus, reduces distractions, and helps complete tasks faster. The structure removes decision fatigue by telling you exactly when to work and when to rest. Breaking work into blocks makes large projects feel manageable. Many people report less procrastination and higher-quality output when using a Pomodoro timer consistently.',
      },
      {
        question: 'What is the best Pomodoro timer?',
        answer: 'The best Pomodoro timer offers customizable intervals, ambient sounds for concentration, and session tracking. Focuzon provides all of these features free of charge. Look for a tool that runs without signup, supports different work and break lengths, and integrates with focus sessions. Avoid timers that require subscriptions or add unnecessary complexity.',
      },
      {
        question: 'Can Pomodoro help with ADHD?',
        answer: 'Pomodoro can help with ADHD by breaking work into short, manageable blocks and reducing overwhelm. The external timer removes the need to track time mentally, which many people with ADHD find difficult. Structured intervals and clear break times support focus and motivation. The technique also creates natural rewards (breaks) that can improve task adherence. Many users report better concentration when using Pomodoro.',
      },
    ],
    internalLinks: [
      { label: 'Deep Work', path: '/deep-work' },
      { label: 'Task Manager', path: '/task-manager' },
      { label: 'Focus Sounds', path: '/focus-sounds' },
    ],
    ctaTitle: 'Stay Focused With Focuzon',
    ctaDescription: 'Focuzon combines Pomodoro timers, deep work sessions, and task management tools to help you complete important work without distraction.',
    howToSteps: [
      { name: 'Start timer', text: 'Choose your task and start the Pomodoro timer for 25 minutes.' },
      { name: 'Work for 25 minutes', text: 'Focus on your task without interruption until the timer rings.' },
      { name: 'Take short break', text: 'Step away for 5 minutes to recharge.' },
      { name: 'Repeat', text: 'After four pomodoros, take a longer 15–30 minute break.' },
    ],
    screenshot: { src: '/images/features/pomodoro-timer-interface.png', alt: 'Pomodoro timer interface in Focuzon productivity app' },
    relatedGuides: [
      { label: 'How to Increase Productivity', path: '/blog/how-to-increase-productivity' },
      { label: 'Deep Work vs Pomodoro', path: '/deep-work' },
    ],
  },
  'deep-work': {
    slug: 'deep-work',
    title: 'Deep Work for Focused Productivity | Focuzon',
    description: 'Practice deep work and stay focused using structured focus sessions with Focuzon.',
    h1: 'Deep Work',
    intro: 'Practice deep work and stay focused using structured focus sessions with Focuzon. Block distractions and produce high-quality work.',
    h2Sections: [
      {
        heading: 'What is Deep Work',
        content: 'Deep work is a concept from Cal Newport that describes focused, uninterrupted work on cognitively demanding tasks. It requires sustained attention without distraction to produce high-quality output. Deep work sessions typically range from 60 to 120 minutes.',
      },
      {
        heading: 'Deep Work vs Multitasking',
        content: 'Deep work is the opposite of multitasking. Multitasking splits attention and reduces quality. Deep work dedicates full focus to one task, leading to better results, faster completion, and less mental fatigue. The deep work timer helps you protect and schedule focused blocks.',
      },
      {
        heading: 'How to Practice Deep Work',
        content: 'Schedule dedicated blocks for deep work, eliminate distractions (notifications, tabs, noise), and use a timer to define session boundaries. Focuzon helps by providing structured sessions, ambient sounds, and a distraction-free environment.',
      },
      {
        heading: 'Start a Deep Work Session',
        content: 'Begin a deep work session with Focuzon. Set your goal, choose your duration, and enter a focused block. Use focus sounds or silence to maintain concentration. Track your sessions over time to build a consistent deep work habit.',
      },
    ],
    faqs: [
      {
        question: 'What is deep work?',
        answer: 'Deep work is a method of focusing on cognitively demanding tasks without distraction to produce high-quality work. Popularized by author Cal Newport, it involves sustained attention for 60–120 minutes on a single task. Unlike multitasking, deep work requires eliminating notifications and external interruptions. It is particularly valuable for writing, coding, analysis, and creative work that benefits from extended concentration.',
      },
      {
        question: 'How long should a deep work session be?',
        answer: 'Deep work sessions typically last 60–120 minutes. Start with 60 minutes if you are new to the practice and extend as your focus capacity grows. The goal is to enter a flow state where you produce your best work. Schedule sessions during your peak energy hours. Take a real break between sessions to maintain quality. Focuzon lets you set custom durations.',
      },
      {
        question: 'Is the Focuzon deep work timer free?',
        answer: 'Yes. Focuzon offers free deep work timers with customizable durations, focus sounds, and session tracking. No subscription or credit card is required. You can set 60, 90, or 120-minute blocks and choose ambient audio to support concentration. Optional account creation saves your session history. The core timer works without signing up.',
      },
      {
        question: 'What is the difference between deep work and Pomodoro?',
        answer: 'Pomodoro uses short 25-minute blocks with frequent breaks; deep work uses longer 60–120 minute blocks for sustained focus on complex tasks. Pomodoro suits varied or administrative work; deep work suits writing, coding, and analytical tasks that need uninterrupted thinking. Both improve productivity. Focuzon supports both so you can choose based on the task.',
      },
      {
        question: 'Does deep work improve productivity?',
        answer: 'Yes. Deep work lets you produce high-quality output faster by dedicating uninterrupted focus to cognitively demanding tasks. Research shows that protected focus time increases output quality and reduces errors. Multitasking and constant context-switching fragment attention and drain mental energy. Deep work sessions help you enter flow state and complete complex work more efficiently.',
      },
      {
        question: 'What is the best deep work timer?',
        answer: 'The best deep work timer offers customizable sessions (60–120 minutes), ambient sounds for concentration, and session tracking. Focuzon provides all of these free. Look for a simple interface that does not distract, supports focus sounds, and optionally saves session history. Avoid tools that require complex setup or subscriptions for basic timer functionality.',
      },
    ],
    internalLinks: [
      { label: 'Pomodoro Timer', path: '/pomodoro-timer' },
      { label: 'Focus Sounds', path: '/focus-sounds' },
      { label: 'Task Manager', path: '/task-manager' },
    ],
    ctaTitle: 'Stay Focused With Focuzon',
    ctaDescription: 'Focuzon combines Pomodoro timers, deep work sessions, and task management tools to help you complete important work without distraction.',
    howToSteps: [
      { name: 'Set your goal', text: 'Choose what you want to accomplish and set your duration (60–120 minutes).' },
      { name: 'Eliminate distractions', text: 'Close notifications, tabs, and set your status to focus mode.' },
      { name: 'Start the timer', text: 'Begin your deep work session and work without interruption.' },
      { name: 'Take a break', text: 'When the session ends, step away to recharge before your next block.' },
    ],
    screenshot: { src: '/images/features/deep-work-session.png', alt: 'Deep work session interface in Focuzon productivity app' },
    relatedGuides: [
      { label: 'How to Increase Productivity', path: '/blog/how-to-increase-productivity' },
      { label: 'Deep Work vs Pomodoro', path: '/pomodoro-timer' },
    ],
  },
  'focus-sounds': {
    slug: 'focus-sounds',
    title: 'Focus Sounds for Productivity | Focuzon',
    description: 'Stay focused with ambient sounds, white noise, and binaural beats. Focuzon focus sounds help you concentrate during work sessions.',
    h1: 'Focus Sounds',
    intro: 'Enhance your concentration with Focuzon focus sounds. Stay productive using ambient sounds, white noise, rain, nature sounds, and binaural beats during Pomodoro sessions and deep work.',
    h2Sections: [
      {
        heading: 'What are Focus Sounds',
        content: 'Focus sounds are ambient audio (white noise, rain, nature sounds, coffee shop ambience, and binaural beats) that help mask distractions and create a calming environment for concentration. They support sustained focus during work sessions by reducing external noise and mental chatter.',
      },
      {
        heading: 'Benefits of Focus Sounds',
        content: 'Focus sounds improve productivity by masking distracting noises, creating a consistent auditory environment, and helping you enter a flow state faster. Different sounds suit different tasks: white noise for deep focus, rain for relaxed concentration, and ambient tones for creative work. Focuzon offers a variety of focus sounds to match your preference.',
      },
      {
        heading: 'How to Use Focus Sounds',
        content: 'Start a Pomodoro or deep work session with Focuzon, then choose your preferred focus sound. Options include white noise, rain, forest, coffee shop, and binaural beats. Adjust the volume to mask distractions without overpowering your thoughts. Use the same sound consistently to train your brain to associate it with focus.',
      },
      {
        heading: 'Start a Focus Session',
        content: 'Start a focus session with Focuzon. Set your goal, choose your focus sounds, and work in a distraction-free environment. Combine focus sounds with Pomodoro or deep work timers for structured productivity.',
      },
    ],
    faqs: [
      {
        question: 'What are focus sounds?',
        answer: 'Focus sounds are ambient audio such as white noise, rain, nature sounds, and binaural beats that help mask distractions and create a calming environment for concentration during work. They block external noise and reduce mental chatter by providing a consistent auditory backdrop. Different sounds suit different tasks: white noise for deep focus, rain for relaxed concentration, and coffee shop ambience for lighter work.',
      },
      {
        question: 'How do Focuzon focus sounds work?',
        answer: 'Focuzon focus sounds let you choose from white noise, rain, forest, coffee shop ambience, and binaural beats. Play them during Pomodoro or deep work sessions to mask distractions and sustain concentration. Set the volume to block background noise without overpowering your thoughts. Use the same sound consistently to train your brain to associate it with focus. Combine with timers for structured productivity.',
      },
      {
        question: 'Are focus sounds free?',
        answer: 'Yes. Focuzon focus sounds are completely free. Use them with Pomodoro timers and deep work sessions to enhance concentration. No signup or subscription is required. All sound options are available immediately. Optional account creation lets you save preferences and session history. The sounds work in your browser without any download or installation.',
      },
      {
        question: 'What focus sounds help with concentration?',
        answer: 'White noise and rain are popular for deep focus because they provide steady, non-distracting sound. Coffee shop ambience suits lighter or creative tasks. Binaural beats may help with specific brainwave states for concentration. Nature sounds like forest or ocean can reduce stress. Choose based on your task and preference; consistency matters more than the specific sound.',
      },
      {
        question: 'Do focus sounds improve productivity?',
        answer: 'Yes. Focus sounds mask distracting noises, create a consistent auditory environment, and can help you enter flow state faster by signaling your brain that it is time to focus. Research suggests ambient sound reduces the cognitive load of filtering out interruptions. Many users report finishing tasks faster and maintaining concentration longer when using focus sounds during work sessions.',
      },
      {
        question: 'What is deep work?',
        answer: 'Deep work is sustained, uninterrupted focus on cognitively demanding tasks for 60–120 minutes. It requires eliminating distractions and dedicating full attention to one task. Focuzon focus sounds support deep work by providing ambient audio that aids concentration and masks environmental noise. Combine focus sounds with deep work timers for optimal conditions for high-quality output.',
      },
    ],
    internalLinks: [
      { label: 'Pomodoro Timer', path: '/pomodoro-timer' },
      { label: 'Deep Work', path: '/deep-work' },
      { label: 'Task Manager', path: '/task-manager' },
    ],
    ctaTitle: 'Stay Focused With Focuzon',
    ctaDescription: 'Focuzon combines Pomodoro timers, deep work sessions, and task management tools to help you complete important work without distraction.',
    howToSteps: [
      { name: 'Start a session', text: 'Begin a Pomodoro or deep work session in Focuzon.' },
      { name: 'Choose your sound', text: 'Select white noise, rain, forest, coffee shop, or binaural beats.' },
      { name: 'Adjust volume', text: 'Set the volume to mask distractions without overpowering your thoughts.' },
      { name: 'Work until complete', text: 'Focus on your task. The ambient sound helps sustain concentration.' },
    ],
    screenshot: { src: '/images/features/focus-sound-interface.jpeg', alt: 'Focus sounds interface in Focuzon productivity app' },
    relatedGuides: [
      { label: 'How to Increase Productivity', path: '/blog/how-to-increase-productivity' },
      { label: 'Deep Work vs Pomodoro', path: '/deep-work' },
    ],
  },
  'task-manager': {
    slug: 'task-manager',
    title: 'Task Management for Focused Work | Focuzon',
    description: 'Task manager built for focused work. Organize tasks and get more done with Focuzon.',
    h1: 'Task Management',
    intro: 'Benefits of task management include clearer priorities, less overwhelm, and better focus. A task manager helps you know what to do next, break large projects into manageable steps, and track what you complete. Focuzon’s task manager integrates with Pomodoro and deep work sessions so you work on the right thing at the right time.',
    h2Sections: [
      {
        heading: 'What is Task Management',
        content: 'Task management is the practice of organizing, prioritizing, and tracking work tasks. Effective task management helps you know what to do next, reduce overwhelm, and complete important work. Focuzon integrates tasks with focus sessions so you work on the right thing at the right time.',
      },
      {
        heading: 'Planning Tasks for Focused Work',
        content: 'Before a focus session, add tasks and prioritize. Link tasks to sessions so you know exactly what to work on. Break large projects into smaller tasks and use focus blocks to complete them one at a time.',
      },
      {
        heading: 'Tracking Productivity',
        content: 'Track which tasks you complete, how much focus time you spend, and your progress over time. Focuzon connects tasks to focus sessions, so you see what you accomplished in each session.',
      },
      {
        heading: 'Start Managing Tasks',
        content: 'Create tasks, attach them to focus sessions, and track completion. Focuzon task manager is designed for deep work: simple, integrated, and focused on getting things done.',
      },
    ],
    faqs: [
      {
        question: 'What is task management?',
        answer: 'Task management is organizing and tracking work tasks so you complete priorities on time. It includes listing tasks, prioritizing by importance and urgency, and scheduling work blocks. Effective task management reduces overwhelm by breaking large projects into smaller steps. It clarifies what to do next and helps you avoid decision fatigue. Focuzon integrates task management with focus sessions for aligned execution.',
      },
      {
        question: 'Does Focuzon have a task manager?',
        answer: 'Yes. Focuzon includes a task manager that integrates with focus sessions. Add tasks, link them to Pomodoro or deep work sessions, and track completion over time. The integration means you know exactly what to work on when you start a focus block. Tasks stay simple and focused on execution rather than complex project management. No separate app required.',
      },
      {
        question: 'Is the task manager free?',
        answer: 'Yes. Focuzon task management is free. Create an optional account to sync tasks and sessions across devices. The core features work without signing up: you can add tasks and link them to focus sessions. Account holders get session history and cross-device sync. No credit card or subscription is required for task management or focus timers.',
      },
      {
        question: 'Can I use tasks with Pomodoro?',
        answer: 'Yes. Focuzon lets you add tasks to focus sessions and work on them during Pomodoro or deep work blocks. Before starting a timer, attach a task to the session so you know what to focus on. This reduces the "what should I do?" decision at the start of each block. Tasks and timers work together to support consistent progress.',
      },
      {
        question: 'What is the best task manager for focus?',
        answer: 'The best task manager for focus integrates with timers and sessions rather than standing alone. Focuzon combines tasks with Pomodoro and deep work so you choose a task, start a focus block, and execute without switching apps. Look for simplicity, timer integration, and minimal friction. Avoid task managers that add complexity without improving focus.',
      },
      {
        question: 'Does task management improve productivity?',
        answer: 'Yes. Task management helps you prioritize, reduce overwhelm, and complete important work by clarifying what to do next. When tasks are listed and ordered, you spend less mental energy deciding and more on execution. Breaking projects into steps makes large goals feel achievable. Focuzon connects tasks to focus sessions so you make progress consistently.',
      },
    ],
    internalLinks: [
      { label: 'Pomodoro Timer', path: '/pomodoro-timer' },
      { label: 'Deep Work', path: '/deep-work' },
      { label: 'Focus Sounds', path: '/focus-sounds' },
    ],
    ctaTitle: 'Stay Focused With Focuzon',
    ctaDescription: 'Focuzon combines Pomodoro timers, deep work sessions, and task management tools to help you complete important work without distraction.',
    howToSteps: [
      { name: 'Add tasks', text: 'Create tasks and optionally link them to projects.' },
      { name: 'Prioritize', text: 'Order tasks by importance and attach them to focus sessions.' },
      { name: 'Start a session', text: 'Begin a Pomodoro or deep work block and work on the linked task.' },
      { name: 'Complete and track', text: 'Mark tasks done and review session history and productivity.' },
    ],
    screenshot: { src: '/images/features/task-manager-dashboard.png', alt: 'Task manager dashboard in Focuzon productivity app' },
    relatedGuides: [
      { label: 'How to Increase Productivity', path: '/blog/how-to-increase-productivity' },
      { label: 'Deep Work vs Pomodoro', path: '/deep-work' },
    ],
  },
  'productivity-tools': {
    slug: 'productivity-tools',
    title: 'Best Productivity Tools for Deep Work | Focuzon',
    description: 'Free productivity tools: timers, invoice generator, calculators, and more. Stay productive with Focuzon.',
    h1: 'Productivity Tools',
    intro: 'Free productivity tools for deep work: timers, task manager, invoice generator, calculators, and more. All in one place with Focuzon.',
    h2Sections: [
      {
        heading: 'What are Productivity Tools',
        content: 'Productivity tools help you work faster, stay organized, and reduce friction. They include timers, task managers, note-taking apps, calculators, and automation. Focuzon offers focus sounds, task management, and free utilities like invoice and password generators.',
      },
      {
        heading: 'Tools for Focused Work',
        content: 'The best productivity tools for deep work support concentration, reduce distractions, and integrate tasks with timers. Focuzon combines Pomodoro, deep work timers, task management, and free tools in one place.',
      },
      {
        heading: 'Free Productivity Tools',
        content: 'Focuzon provides free productivity tools: Pomodoro timer, deep work, focus sounds, task manager, invoice generator, password generator, currency converter, EMI calculator, and word counter. No credit card required.',
      },
      {
        heading: 'Start Using Productivity Tools',
        content: 'Use Focuzon productivity tools to stay focused and get more done. Start a focus session, manage tasks, or try free utilities. Sign up to save progress and sync across devices.',
      },
    ],
    faqs: [
      {
        question: 'What are productivity tools?',
        answer: 'Productivity tools are apps and methods that help you work more efficiently. They include focus timers (Pomodoro, deep work), task managers, and utilities like calculators, converters, and generators. Good tools reduce friction, clarify priorities, and support consistent work habits. Focuzon combines timers, tasks, and free utilities in one place so you do not need multiple apps for basic productivity needs.',
      },
      {
        question: 'Are Focuzon productivity tools free?',
        answer: 'Yes. Focuzon offers free focus timers, task management, and utilities such as password generator, currency converter, word counter, and more. No signup is required for most tools; you can start using them immediately. Optional account creation enables session history and cross-device sync for timers and tasks. No credit card or subscription is needed.',
      },
      {
        question: 'Which productivity tools does Focuzon offer?',
        answer: 'Focuzon includes Pomodoro timer, deep work sessions, focus sounds, task manager, password generator, currency converter, unit converter, word counter, and QR code generator. All tools are free and designed for quick access. No signup is required for individual tools. The focus features (timers, tasks) integrate with each other for a unified productivity experience.',
      },
      {
        question: 'What is deep work?',
        answer: 'Deep work is sustained focus on cognitively demanding tasks for 60–120 minutes without distraction. Popularized by Cal Newport, it produces high-quality output faster than fragmented work. Focuzon offers deep work timers alongside Pomodoro and other productivity tools. Use focus sounds and a calm interface to support concentration. Schedule deep work during your peak energy hours for best results.',
      },
      {
        question: 'How to focus for 8 hours?',
        answer: 'Use structured sessions (Pomodoro or deep work), planned breaks, and a task manager. Focuzon combines these to support long focus days. Alternate 60–90 minute deep work blocks with 15–20 minute breaks. Do not try to focus for 8 hours straight; the brain needs rest. Start with 4–6 hours of protected focus time and expand as your stamina grows.',
      },
      {
        question: 'Best productivity apps for students?',
        answer: 'Best apps include Pomodoro timers for study blocks, task managers for assignments, and utilities for writing and research. Focuzon offers Pomodoro, focus sounds, word counter, and free tools suitable for students. The simple interface reduces distraction. No premium tiers or subscriptions; students can use everything free. Combine with a quiet study space for best results.',
      },
    ],
    internalLinks: [
      { label: 'Pomodoro Timer', path: '/pomodoro-timer' },
      { label: 'Deep Work', path: '/deep-work' },
      { label: 'Focus Sounds', path: '/focus-sounds' },
      { label: 'Task Manager', path: '/task-manager' },
    ],
    ctaTitle: 'Stay Focused With Focuzon',
    ctaDescription: 'Focuzon combines Pomodoro timers, deep work sessions, and task management tools to help you complete important work without distraction.',
    howToSteps: [
      { name: 'Choose a tool', text: 'Pick a timer, task manager, or utility based on your goal.' },
      { name: 'Set up your session', text: 'Configure duration, tasks, or inputs for the tool.' },
      { name: 'Work or use the tool', text: 'Execute your task or use the utility as needed.' },
      { name: 'Track and iterate', text: 'Review sessions or results and adjust for next time.' },
    ],
    relatedGuides: [
      { label: 'How to Increase Productivity', path: '/blog/how-to-increase-productivity' },
      { label: 'Deep Work vs Pomodoro', path: '/deep-work' },
    ],
  },
  'distraction-blocker': {
    slug: 'distraction-blocker',
    title: 'Distraction Blocker for Focused Work | Focuzon',
    description: 'Block distractions and stay in flow with Focuzon. Structured focus sessions reduce interruptions.',
    h1: 'Distraction Blocker',
    intro: 'Block distractions and stay in flow with Focuzon. Structured focus sessions and a calm interface reduce interruptions and support deep work.',
    h2Sections: [
      {
        heading: 'What is a Distraction Blocker',
        content: 'A distraction blocker helps you stay focused by limiting or blocking access to distracting websites, apps, and notifications during work. Focuzon uses structured focus sessions and a calm interface to reduce digital and mental distractions.',
      },
      {
        heading: 'Benefits of Blocking Distractions',
        content: 'Blocking distractions increases focus time, improves task completion, and reduces context-switching. A dedicated focus session signals to yourself and others that you are in deep work mode, reducing interruptions.',
      },
      {
        heading: 'How Focuzon Helps Block Distractions',
        content: 'Focuzon creates a dedicated focus environment with timers, ambient sounds, and a simple interface. Entering a session helps you commit to focused work. Combine with browser extensions or Do Not Disturb for full protection.',
      },
      {
        heading: 'Start a Distraction-Free Session',
        content: 'Begin a focus session with Focuzon. Set your goal, start the timer, and work in a calm, structured environment. Use focus sounds to mask noise and breaks to recharge without losing momentum.',
      },
    ],
    faqs: [
      {
        question: 'What is a distraction blocker?',
        answer: 'A distraction blocker limits access to websites, apps, or notifications during work to help you stay focused and complete tasks faster. It can be a browser extension, app blocker, or a structured focus session that signals you are in work mode. The goal is to reduce interruptions that fragment attention and delay task completion. Focuzon uses focus sessions and a calm interface as a form of distraction blocking.',
      },
      {
        question: 'Does Focuzon block distractions?',
        answer: 'Focuzon helps block distractions by providing structured focus sessions, a calm interface, and a commitment to single-tasking. When you start a session, you commit to one task. The minimal UI reduces visual clutter. Combine with your device’s Do Not Disturb mode for best results. Focuzon does not block websites directly; it creates an environment and ritual that supports focus.',
      },
      {
        question: 'Is the distraction blocker free?',
        answer: 'Yes. Focuzon focus sessions and distraction-reducing features are free. No subscription or credit card is required. Create an optional account to track session history. The core experience (timers, focus sounds, minimal interface) works without signing up. Use it with Pomodoro or deep work blocks to create protected focus time throughout your day.',
      },
      {
        question: 'How do I block distractions when working?',
        answer: 'Use focus sessions with a timer, turn on Do Not Disturb on your devices, and choose a calm environment. Focuzon provides structured sessions and minimal UI to reduce interruptions. Close unnecessary tabs and silence notifications. Tell others you are in focus mode. Combine environmental control (quiet space) with tool support (Focuzon timers) for consistent results.',
      },
      {
        question: 'Does blocking distractions improve productivity?',
        answer: 'Yes. Reducing distractions increases focus time and task completion. Research shows interruptions extend task duration and increase errors. Structured sessions help you commit to single-tasking and signal to others that you are unavailable. Even partial distraction reduction (fewer notifications, fewer tabs) improves output quality. Focuzon timers create clear boundaries for protected work time.',
      },
      {
        question: 'What is deep work?',
        answer: 'Deep work is sustained, distraction-free focus on cognitively demanding tasks for 60–120 minutes. It requires eliminating notifications and external interruptions. Focuzon helps create the conditions for deep work with timers, focus sounds, and a focused interface. Use it to schedule and protect blocks for writing, coding, analysis, or creative work that benefits from extended concentration.',
      },
    ],
    internalLinks: [
      { label: 'Pomodoro Timer', path: '/pomodoro-timer' },
      { label: 'Deep Work', path: '/deep-work' },
      { label: 'Focus Sounds', path: '/focus-sounds' },
      { label: 'Task Manager', path: '/task-manager' },
    ],
    ctaTitle: 'Stay Focused With Focuzon',
    ctaDescription: 'Focuzon combines Pomodoro timers, deep work sessions, and task management tools to help you complete important work without distraction.',
    howToSteps: [
      { name: 'Set your goal', text: 'Decide what you want to accomplish in this focus block.' },
      { name: 'Start a session', text: 'Begin a focus session. This signals commitment and reduces interruptions.' },
      { name: 'Work without distraction', text: 'Focus on your task. Use minimal UI and optional focus sounds.' },
      { name: 'Take breaks', text: 'When the session ends, step away before starting the next block.' },
    ],
    screenshot: { src: '/images/features/distraction-blocker-session.png', alt: 'Distraction-free focus session in Focuzon' },
    relatedGuides: [
      { label: 'How to Increase Productivity', path: '/blog/how-to-increase-productivity' },
      { label: 'Deep Work vs Pomodoro', path: '/deep-work' },
    ],
  },
  'work-session-timer': {
    slug: 'work-session-timer',
    title: 'Work Session Timer for Productivity | Focuzon',
    description: 'Track work sessions and focus time with the Focuzon. Improve productivity with timed blocks.',
    h1: 'Work Session Timer',
    intro: 'Track work sessions and focus time with the Focuzon. Improve productivity with timed blocks, analytics, and session history.',
    h2Sections: [
      {
        heading: 'What is a Work Session Timer',
        content: 'A work session timer allocates dedicated time blocks for work and tracks how much time you spend on focused tasks. It helps you commit to sessions, avoid overwork, and measure productivity. Focuzon work session timer supports customizable lengths and task integration.',
      },
      {
        heading: 'Benefits of Work Session Tracking',
        content: 'Tracking work sessions increases accountability, reveals how much focus time you actually get, and helps you schedule realistic blocks. It supports better time management and reduces burnout by enforcing breaks.',
      },
      {
        heading: 'How to Use Work Session Timer',
        content: 'Set a work session goal, choose duration (e.g., 25, 45, or 60 minutes), and start the timer. Work without interruption until the session ends, then take a break. Focuzon records sessions for analytics and streaks.',
      },
      {
        heading: 'Start a Work Session',
        content: 'Start a work session with Focuzon. Add tasks, set duration, and begin. Use focus sounds or silence. Track your completed sessions and see your productivity trends over time.',
      },
    ],
    faqs: [
      {
        question: 'What is a work session timer?',
        answer: 'A work session timer allocates specific time blocks for work and tracks focus time. It helps maintain concentration by creating clear start and end boundaries. You commit to working for a set duration, then take a break. Tracking reveals how much focused work you actually do. Focuzon supports customizable session lengths (25, 45, 60, 90 minutes) and optional task integration.',
      },
      {
        question: 'How does Focuzon work session timer work?',
        answer: 'Focuzon work session timer lets you set goals, durations, and optional tasks. Start a session, work until the timer ends, then take a break. Sessions are saved for analytics when you have an account. You can choose focus sounds or silence. The timer integrates with tasks so you know what to work on. Review session history to see productivity trends over time.',
      },
      {
        question: 'Is work session tracking free?',
        answer: 'Yes. Focuzon work session timer is free. No subscription or credit card is required. Create an optional account to save session history and view analytics. The core timer works without signing up. You get customizable durations, focus sounds, and a simple interface. Account holders get session history, streaks, and cross-device sync.',
      },
      {
        question: 'How long should a work session be?',
        answer: 'Work sessions can be 25 minutes (Pomodoro style), 45–60 minutes (standard focus), or 90–120 minutes (deep work). Choose based on task complexity and your attention span. Start with 25 or 45 minutes if you are building focus habits. Extend to 60–90 minutes for complex tasks that benefit from sustained concentration. Take real breaks between sessions.',
      },
      {
        question: 'Does work session tracking improve productivity?',
        answer: 'Yes. Tracking sessions increases accountability, reveals actual focus time, and helps you plan realistic work blocks. Many people overestimate how much they work; a timer shows the truth. Regular sessions build a focus habit. Analytics help you identify your most productive times. Focuzon combines timers with optional task tracking for clearer progress visibility.',
      },
      {
        question: 'What is deep work?',
        answer: 'Deep work is uninterrupted focus on cognitively demanding tasks for 60–120 minutes. Popularized by Cal Newport, it produces high-quality output. Focuzon work session timer supports deep work blocks with customizable durations and analytics. Use focus sounds and a calm interface. Schedule deep work during peak energy hours. Take genuine breaks between blocks to maintain quality.',
      },
    ],
    internalLinks: [
      { label: 'Pomodoro Timer', path: '/pomodoro-timer' },
      { label: 'Deep Work', path: '/deep-work' },
      { label: 'Focus Sounds', path: '/focus-sounds' },
      { label: 'Task Manager', path: '/task-manager' },
    ],
    ctaTitle: 'Stay Focused With Focuzon',
    ctaDescription: 'Focuzon combines Pomodoro timers, deep work sessions, and task management tools to help you complete important work without distraction.',
    howToSteps: [
      { name: 'Set your goal', text: 'Choose what to work on and add optional tasks.' },
      { name: 'Set duration', text: 'Pick session length (25, 45, 60, or 90 minutes).' },
      { name: 'Start the timer', text: 'Begin the work session and focus until it ends.' },
      { name: 'Review analytics', text: 'Check session history and productivity trends.' },
    ],
    screenshot: { src: '/images/features/work-session-timer.png', alt: 'Work session timer and analytics in Focuzon' },
    relatedGuides: [
      { label: 'How to Increase Productivity', path: '/blog/how-to-increase-productivity' },
      { label: 'Deep Work vs Pomodoro', path: '/deep-work' },
    ],
  },
};

import { FREE_TOOLS_LINKS } from './freeTools';

export { FREE_TOOLS_LINKS };

export function getFeatureSEO(slug: FeatureSlug): FeatureSEOConfig | null {
  return FEATURE_SEO_DATA[slug] ?? null;
}

export function getSoftwareApplicationSchema(featureName: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Focuzon',
    applicationCategory: 'ProductivityApplication',
    featureList: [
      'Pomodoro Timer',
      'Deep Work',
      'Focus Sounds',
      'Task Manager',
      'Focus Sessions',
      'Distraction Reduction',
      'Work Session Tracking',
    ],
    description: `Focuzon ${featureName} for focused work. Free productivity tools and timers.`,
    url: 'https://focuzon.com',
  };
}

export function getFAQSchema(faqs: FAQItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function getHowToSchema(name: string, steps: HowToStep[], url?: string) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to use ${name}`,
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
  if (url) schema.url = url;
  return schema;
}

export { FEATURE_SEO_DATA };
